/**
 * Legal guardians / legal representatives service (Phase 6, PAT-02).
 * Routes /patientmr/{patientId}/legal-guardians — separate from the family-portal
 * guardian accounts at /patients/{id}/guardians.
 */

import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { LegalGuardian, LegalGuardianInput } from '../../dtos/patientmr/legalGuardian.dto';

type GuardianListPayload = LegalGuardian[] | { guardians?: LegalGuardian[]; items?: LegalGuardian[] };
type GuardianItemPayload = LegalGuardian | { guardian?: LegalGuardian };

const unwrapItem = (data: GuardianItemPayload | undefined): LegalGuardian | null => {
  if (!data) return null;
  if ('guardianId' in data) return data;
  return data.guardian ?? null;
};

const byContactOrder = (a: LegalGuardian, b: LegalGuardian) => (a.contactOrder ?? 0) - (b.contactOrder ?? 0);

export class LegalGuardiansService {
  /** List a patient's legal guardians, ordered by contactOrder. */
  static async list(patientId: string): Promise<LegalGuardian[]> {
    const response: ApiResponse<GuardianListPayload> = await apiClient.get(
      `/patientmr/${patientId}/legal-guardians`
    );
    if (response.success) {
      const data = response.data;
      const rows = Array.isArray(data) ? data : data?.guardians || data?.items || [];
      return [...rows].sort(byContactOrder);
    }
    throw new Error(response.error?.message || 'Failed to load guardians');
  }

  static async create(patientId: string, input: LegalGuardianInput): Promise<LegalGuardian | null> {
    const response: ApiResponse<GuardianItemPayload> = await apiClient.post(
      `/patientmr/${patientId}/legal-guardians`,
      input
    );
    if (response.success) return unwrapItem(response.data);
    throw new Error(response.error?.message || 'Failed to add guardian');
  }

  static async update(patientId: string, guardianId: string, input: LegalGuardianInput): Promise<LegalGuardian | null> {
    const response: ApiResponse<GuardianItemPayload> = await apiClient.put(
      `/patientmr/${patientId}/legal-guardians/${guardianId}`,
      input
    );
    if (response.success) return unwrapItem(response.data);
    throw new Error(response.error?.message || 'Failed to update guardian');
  }

  static async remove(patientId: string, guardianId: string): Promise<void> {
    const response: ApiResponse<unknown> = await apiClient.delete(
      `/patientmr/${patientId}/legal-guardians/${guardianId}`
    );
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove guardian');
    }
  }
}

export default LegalGuardiansService;
